import React from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  SimpleGrid,
  Accordion,
} from '@chakra-ui/react';
import { 
  FaArrowLeft, 
  FaUserPlus, 
  FaSearch, 
  FaComments, 
  FaHandshake,
  FaStar,
  FaCalendar,
  FaMusic,
  FaUsers,
  FaGlobe,
} from 'react-icons/fa';
import Footer from './Footer';
import SEO from './SEO.jsx';

const ComoFunciona = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const pasosCofradia = [
    {
      icon: FaUserPlus,
      title: 'Regístrate',
      description: 'Crea tu cuenta como cofradía y completa tu perfil con la información de tu hermandad.',
    },
    {
      icon: FaCalendar,
      title: 'Crea un Evento',
      description: 'Publica los detalles de tu procesión: fecha, ubicación y requisitos.',
    },
    {
      icon: FaSearch,
      title: 'Busca Bandas',
      description: 'Explora bandas cercanas, filtra por tipo, disponibilidad y proximidad. Ve perfiles, reseñas y repertorios.',
    },
    {
      icon: FaComments,
      title: 'Inicia Conversación',
      description: 'Contacta directamente con las bandas que te interesen. Chatea en tiempo real.',
    },
    {
      icon: FaHandshake,
      title: 'Propón un Acuerdo',
      description: 'Negocia precio y condiciones. Una vez acordado, ambas partes confirman el acuerdo.',
    },
    {
      icon: FaStar,
      title: 'Deja una Reseña',
      description: 'Después del evento, valora la experiencia. Ayuda a otras cofradías a tomar decisiones.',
    },
  ];

  const pasosBanda = [
    {
      icon: FaUserPlus,
      title: 'Regístrate y valida',
      description: 'Crea la cuenta, completa los datos obligatorios del perfil y envía el código de validación por mensaje directo a nuestras redes.',
    },
    {
      icon: FaStar,
      title: 'Elige tu plan',
      description: 'Gratis, Estándar, Intermedio o Avanzado. Cada uno define archivo, IA y almacenamiento. Avanzado es para bandas validadas (gratis en lanzamiento).',
    },
    {
      icon: FaUsers,
      title: 'Monta la plantilla',
      description: 'Añade músicos y formaciones. Sin eso no se pueden programar ensayos ni actuaciones.',
    },
    {
      icon: FaMusic,
      title: 'Archivo, ensayos y actuaciones',
      description: 'Sube partituras (la IA las organiza), pasa lista, comparte encuestas de asistencia y consulta estadísticas u objetivos.',
    },
    {
      icon: FaGlobe,
      title: 'Tu Web y documentos',
      description: 'Publica el micrositio de la banda y, si quieres, un tablón de documentos con contraseña para la agrupación.',
    },
    {
      icon: FaHandshake,
      title: 'Comunidad (opcional)',
      description: 'Calendario, eventos, chat y acuerdos con cofradías. Sigue ahí, pero el día a día de la banda es la Gestión.',
    },
  ];

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <SEO
        title="Cómo Funciona"
        description="Cómo una banda gestiona plantilla, archivo y web pública en Cadencya, y cómo una hermandad encuentra agrupaciones."
        path="/como-funciona"
      />
      {/* Header */}
      <Box bg="purple.600" color="white" py={6}>
        <Container maxW="7xl">
          <Button
            variant="ghost"
            color="white"
            _hover={{ bg: 'purple.700' }}
            mb={4}
            asChild><RouterLink to="/"><Icon asChild><FaArrowLeft /></Icon>Volver al inicio
                                    </RouterLink></Button>
          <Heading size="2xl" mb={4}>
            ¿Cómo Funciona Cadencya?
          </Heading>
          <Text fontSize="xl" maxW="3xl">
            Primero, el sistema de la agrupación: plantilla, partituras, ensayos y web pública.
            Después, la comunidad para hermandades y entidades.
          </Text>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Box flex="1" bg={bgColor} py={16}>
        <Container maxW="7xl">
          {/* Pasos para Bandas */}
          <Box mb={20}>
            <Heading size="xl" color="purple.600" textAlign="center" mb={4}>
              Para Bandas de Música
            </Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" mb={12} maxW="3xl" mx="auto">
              Gestiona la agrupación (plantilla, partituras, ensayos) y, si quieres, conecta con cofradías
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8}>
              {pasosBanda.map((paso, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={8}
                  rounded="2xl"
                  shadow="lg"
                  position="relative"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-5px)', shadow: 'xl' }}
                >
                  <VStack align="start" gap={4}>
                    <Box
                      bg="blue.100"
                      p={4}
                      rounded="full"
                      display="inline-flex"
                    >
                      <Icon w={6} h={6} color="blue.600" asChild><paso.icon /></Icon>
                    </Box>
                    <VStack align="start" gap={2}>
                      <HStack>
                        <Box
                          bg="blue.600"
                          color="white"
                          fontWeight="bold"
                          rounded="full"
                          w={8}
                          h={8}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="sm"
                        >
                          {index + 1}
                        </Box>
                        <Heading size="md">{paso.title}</Heading>
                      </HStack>
                      <Text color="gray.600">
                        {paso.description}
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* Pasos para Cofradías */}
          <Box>
            <Heading size="xl" color="purple.600" textAlign="center" mb={4}>
              Para Cofradías
            </Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" mb={12} maxW="3xl" mx="auto">
              Organiza tus procesiones, encuentra la banda perfecta y gestiona todo desde un solo lugar
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8}>
              {pasosCofradia.map((paso, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={8}
                  rounded="2xl"
                  shadow="lg"
                  position="relative"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-5px)', shadow: 'xl' }}
                >
                  <VStack align="start" gap={4}>
                    <Box
                      bg="purple.100"
                      p={4}
                      rounded="full"
                      display="inline-flex"
                    >
                      <Icon w={6} h={6} color="purple.600" asChild><paso.icon /></Icon>
                    </Box>
                    <VStack align="start" gap={2}>
                      <HStack>
                        <Box
                          bg="purple.600"
                          color="white"
                          fontWeight="bold"
                          rounded="full"
                          w={8}
                          h={8}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="sm"
                        >
                          {index + 1}
                        </Box>
                        <Heading size="md">{paso.title}</Heading>
                      </HStack>
                      <Text color="gray.600">
                        {paso.description}
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* FAQ */}
          <Box mt={20}>
            <Heading size="xl" color="purple.600" textAlign="center" mb={12}>
              Preguntas Frecuentes
            </Heading>
            <Box maxW="4xl" mx="auto">
              <Accordion.Root collapsible>
                <Accordion.Item border="none" mb={4} value='item-0'>
                  <Accordion.ItemTrigger
                    bg={cardBg}
                    _hover={{ bg: 'purple.50' }}
                    rounded="xl"
                    p={6}
                  >
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="600" fontSize="lg">
                        ¿Es gratuito usar Cadencya?
                      </Text>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent bg={cardBg} p={6} rounded="xl" mt={2}><Accordion.ItemBody>
                      <Text color="gray.600">
                        El registro es gratuito. Las bandas tienen planes (Gratis, Estándar, Intermedio, Avanzado)
                        con distintos topes de archivo, análisis con IA y almacenamiento. Las cofradías usan un
                        plan propio, también sin pago de momento. Puedes ver el detalle en Mi suscripción.
                      </Text>
                    </Accordion.ItemBody></Accordion.ItemContent>
                </Accordion.Item>

                <Accordion.Item border="none" mb={4} value='item-1'>
                  <Accordion.ItemTrigger
                    bg={cardBg}
                    _hover={{ bg: 'purple.50' }}
                    rounded="xl"
                    p={6}
                  >
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="600" fontSize="lg">
                        ¿Cómo me verifico como banda o cofradía?
                      </Text>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent bg={cardBg} p={6} rounded="xl" mt={2}><Accordion.ItemBody>
                      <Text color="gray.600">
                        Completa los datos obligatorios del perfil: se genera un código de validación.
                        Envíalo por mensaje directo a cualquiera de nuestros perfiles en redes. Mientras
                        el equipo lo revisa (suele ser en 48 horas), el perfil no es visible en Comunidad.
                      </Text>
                    </Accordion.ItemBody></Accordion.ItemContent>
                </Accordion.Item>

                <Accordion.Item border="none" mb={4} value='item-2'>
                  <Accordion.ItemTrigger
                    bg={cardBg}
                    _hover={{ bg: 'purple.50' }}
                    rounded="xl"
                    p={6}
                  >
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="600" fontSize="lg">
                        ¿Qué pasa si cancelo un evento?
                      </Text>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent bg={cardBg} p={6} rounded="xl" mt={2}><Accordion.ItemBody>
                      <Text color="gray.600">
                        Si cancelas un evento, todas las bandas con las que hayas iniciado conversación 
                        recibirán una notificación automática con el motivo de la cancelación. 
                        Los acuerdos activos se cancelarán automáticamente.
                      </Text>
                    </Accordion.ItemBody></Accordion.ItemContent>
                </Accordion.Item>

                <Accordion.Item border="none" mb={4} value='item-3'>
                  <Accordion.ItemTrigger
                    bg={cardBg}
                    _hover={{ bg: 'purple.50' }}
                    rounded="xl"
                    p={6}
                  >
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="600" fontSize="lg">
                        ¿Cómo funcionan las reseñas?
                      </Text>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent bg={cardBg} p={6} rounded="xl" mt={2}><Accordion.ItemBody>
                      <Text color="gray.600">
                        Después de que un evento se complete, tanto la banda como la cofradía pueden 
                        dejar una reseña mutua. Las reseñas son bidireccionales y ayudan a construir 
                        reputación en la plataforma. Puedes elegir si publicar tus reseñas o mantenerlas privadas.
                      </Text>
                    </Accordion.ItemBody></Accordion.ItemContent>
                </Accordion.Item>
              </Accordion.Root>
            </Box>
          </Box>

          {/* CTA */}
          <Box textAlign="center" mt={20}>
            <VStack gap={6}>
              <Heading size="lg">
                ¿Listo para empezar?
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Crea la cuenta de tu banda y monta plantilla, archivo y web pública.
              </Text>
              <HStack gap={4}>
                <Button colorPalette="purple" size="lg" rounded="full" px={8} asChild><RouterLink to="/registro?tipo=banda">Registrarse
                                  </RouterLink></Button>
                <Button
                  variant="outline"
                  colorPalette="purple"
                  size="lg"
                  rounded="full"
                  px={8}
                  asChild><RouterLink to="/login">Iniciar Sesión
                                  </RouterLink></Button>
              </HStack>
            </VStack>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default ComoFunciona;
